import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import Axios from "axios";
import {
  CRUDLayout,
  Alert,
  DataStatus,
  ActionButton,
  ReadButton,
  UpdateButton,
  InputSearch,
  Pagination,
  Table,
  THead,
  TBody,
  Tr,
  ThFixed,
  TdFixed,
  Th,
  Td,
  Checkbox,
  ApprovalStatusButton,
} from "components";
import {
  DateConvert,
  RupiahConvert,
  PageNumber as TableNumber,
} from "utilities";
import { ApprovalPurchaseOrderApi } from "api";

const ApprovalPurchaseOrder = () => {
  const history = useHistory();
  const location = useLocation();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [dataRAE, setDataRAE] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    page: "1",
    dataLength: "10",
    totalPage: "1",
    dataCount: "0",
  });
  const [searchConfig, setSearchConfig] = useState({
    status: false,
    key: "",
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "primary",
    text: "",
  });
  const [searchTime, setSearchTime] = useState(null);

  const getInitialData = () => {
    setIsPageLoading(true);

    ApprovalPurchaseOrderApi.getHistory({
      q: searchConfig.key,
      page: paginationConfig.page,
      per_page: paginationConfig.dataLength,
    })
      .then(({ data }) => {
        const dataRAE = data.data;
        setDataRAE(dataRAE ? dataRAE : []);
        setPaginationConfig({
          ...paginationConfig,
          dataCount: data.data_count,
          totalPage: data.total_page,
        });
        // searchConfig.status &&
        //   setAlertConfig({
        //     show: true,
        //     variant: "primary",
        //     text: `Hasil dari pencarian: ${searchConfig.key}`,
        //   });
      })
      .catch((err) => {
        setAlertConfig({
          show: true,
          variant: "danger",
          text: "Data gagal dimuat!",
        });
      })
      .finally(() => setIsPageLoading(false));
  };
  
  const onInputSearchChange = (e) => {
    const key = e.target.value;
    if(searchTime) clearTimeout(searchTime);
    setSearchTime(
      setTimeout(() => {
        setSearchConfig({
          ...searchConfig,
          key: e.target.value,
        });
        setAlertConfig({
          show: key ? true : false,
          variant: "primary",
          text: "Hasil dari pencarian: " + key,
        });
      }, 750)
    )
  }

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState) {
      if (locationState.alert) {
        setAlertConfig({
          show: locationState.alert.show,
          text: locationState.alert.text,
          variant: locationState.alert.variant,
        });
      }
    }
  };

  const generateStatus = (status) => {
    if (status.toUpperCase() === "REV") {
      return "REVISI";
    }

    if (status.toUpperCase() === "VER") {
      return "VERIFIED";
    }

    if (status.toUpperCase() === "APP") {
      return "APPROVED";
    }

    return "PENDING";
  };

  useEffect(() => {
    getInitialData();
    checkAlert();

    return () => {
      setIsPageLoading(false);
    };
  }, [searchConfig.key, paginationConfig.page, paginationConfig.dataLength]);

  const PageContent = () => {
    const getStatusApprovalLabel = (status_approval) => {
      switch (status_approval) {
        case "APP":
          return {
            variant: "outline-success",
            label: "APPROVED",
          };
        case "REJ":
          return {
            variant: "outline-danger",
            label: "REJECT",
          };
        case "REV":
          return {
            variant: "outline-warning",
            label: "REVISI",
          };
        case "VER":
          return {
            variant: "outline-success",
            label: "VERIFIED",
          };
        default:
          return {
            variant: "outline-secondary",
            label: "PENDING",
          };
      }
    };

    const groupedData = dataRAE.reduce((acc, curr) => {
      if (acc[curr.id_approval_transaksi]) {
        acc[curr.id_approval_transaksi].count++;
        acc[curr.id_approval_transaksi].data.push(curr);
      } else {
        acc[curr.id_approval_transaksi] = { count: 1, data: [curr] };
      }
      return acc;
    }, {});

    const tableData = Object.values(groupedData).flatMap(
      ({ count, data }, ind) =>
        data.map((item, index) => ({
          ...item,
          rowspan: index === 0 ? count : 0,
          no: ind + 1,
        }))
    );

    const DataTable = () => (
      <Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            <Th>Informasi Pembuat Pengajuan</Th>
            <Th>Informasi Purchase Order</Th>
            <Th>Vendor</Th>
            <Th>Informasi Seleksi Vendor</Th>
            <Th>Item Barang</Th>
            <Th>Grand Total</Th>
            <Th>Status Approval</Th>
            <Th>Aksi</Th>
          </Tr>
        </THead>
        <TBody>
          {tableData.map((val, index) => (
            <Tr key={index}>
              {val.rowspan !== 0 && (
                <>
                  <TdFixed rowSpan={val.rowspan}>{val.no}</TdFixed>
                  <Td rowSpan={val.rowspan}>
                    <div>{val.nama_karyawan_pengaju ?? "-"}</div>
                    <div>{val.nama_jabatan_pengaju ?? "-"}</div>
                  </Td>
                  <Td rowSpan={val.rowspan}>
                    <div>
                      {val.tgl_purchase_order
                        ? DateConvert(new Date(val.tgl_purchase_order))
                            .defaultDMY
                        : "-"}
                    </div>
                    <div>{val.no_purchase_order ?? "-"}</div>
                  </Td>
                  <Td rowSpan={val.rowspan}>{val.nama_vendor ?? "-"}</Td>
                </>
              )}
              <Td>
                <div>
                  {val.tgl_seleksi_vendor
                    ? DateConvert(new Date(val.tgl_seleksi_vendor)).defaultDMY
                    : "-"}
                </div>
                <div>{val.no_seleksi_vendor ?? "-"}</div>
              </Td>
              <Td>{val.nama_item ?? "-"}</Td>
              {val.rowspan !== 0 && (
                <>
                  <Td className="text-right" rowSpan={val.rowspan}>
                    {
                      RupiahConvert(parseFloat(val.grand_total).toString())
                        .getWithComa
                    }
                  </Td>
                  <TdFixed rowSpan={val.rowspan}>
                    <ApprovalStatusButton
                      variant={
                        getStatusApprovalLabel(val.status_approval).variant
                      }
                    >
                      {getStatusApprovalLabel(val.status_approval).label}
                    </ApprovalStatusButton>
                  </TdFixed>
                  <Td rowSpan={val.rowspan}>
                    <div className="d-flex justify-content-center">
                      <ActionButton
                        size="sm"
                        text="Lihat Detail"
                        className="col"
                        onClick={() =>
                          history.push(
                            "/human-resource/approval/purchase-order/detail",
                            {
                              no_po: val.no_purchase_order,
                              tab: "history",
                            }
                          )
                        }
                      />
                    </div>
                  </Td>
                </>
              )}
            </Tr>
          ))}
        </TBody>
      </Table>
    );

    return (
      <>
        <DataTable />
        {!searchConfig.status && (
          <Pagination
            dataLength={paginationConfig.dataLength}
            dataNumber={
              paginationConfig.page * paginationConfig.dataLength -
              paginationConfig.dataLength +
              1
            }
            dataPage={
              paginationConfig.dataCount < paginationConfig.dataLength
                ? paginationConfig.dataCount
                : paginationConfig.page * paginationConfig.dataLength
            }
            dataCount={paginationConfig.dataCount}
            currentPage={paginationConfig.page}
            totalPage={paginationConfig.totalPage}
            onPaginationChange={({ selected }) =>
              setPaginationConfig({
                ...paginationConfig,
                page: selected + 1,
              })
            }
            onDataLengthChange={(e) =>
              setPaginationConfig({
                ...paginationConfig,
                page: 1,
                dataLength: e.target.value,
              })
            }
          />
        )}
      </>
    );
  };

  return (
    <CRUDLayout>
      {/* head section */}
      <CRUDLayout.Head>
        <CRUDLayout.HeadSearchSection>
          <Row className="mb-2">
            <Col md={8}>
              <InputSearch
                // value={searchConfig.key}
                onChange={onInputSearchChange}
              />
            </Col>
          </Row>
        </CRUDLayout.HeadSearchSection>
        <CRUDLayout.HeadButtonSection></CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* alert */}
      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      />

      {/* content */}
      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : dataRAE ? (
        dataRAE.length > 0 ? (
          <PageContent />
        ) : (
          <DataStatus text="Tidak ada data" />
        )
      ) : (
        <DataStatus text="Data gagal dimuat" />
      )}
    </CRUDLayout>
  );
};

export default ApprovalPurchaseOrder;
